import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';
import ExcelImporter from './components/ExcelImporter';
import ControlPanel from './components/ControlPanel';
import LoginModal from './components/LoginModal';
import { useAuth } from './contexts/AuthContext';
import supabase from './lib/supabase';

const { FiGift, FiMapPin, FiChevronDown, FiCheck, FiPercent, FiCopy, FiExternalLink, FiUpload, FiSettings, FiPlus, FiLoader, FiList, FiGrid, FiSearch, FiX } = FiIcons;

// Table names with random suffix to avoid conflicts
const COUNTRIES_TABLE = 'countries_2gx7qlnvp3';
const COUPONS_TABLE = 'coupons_2gx7qlnvp3';
const BRAND_SETTINGS_TABLE = 'brand_settings_2gx7qlnvp3';

function App() {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [copiedAnimation, setCopiedAnimation] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'import' or 'manage'
  const [coupons, setCoupons] = useState({});
  const [loading, setLoading] = useState(true);
  const codeRefs = useRef({});
  const [countrySelected, setCountrySelected] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated } = useAuth();
  const [showAdminButtons, setShowAdminButtons] = useState(false); // New state to control visibility of admin buttons

  const [brandSettings, setBrandSettings] = useState({
    name: 'CouponHub',
    tagline: 'Best deals in your region',
    logo: '',
    primaryColor: '#6366F1',
    secondaryColor: '#8B5CF6'
  });

  // Update document title and favicon when brand settings change
  useEffect(() => {
    // Update page title with brand name and tagline
    document.title = `${brandSettings.name} - ${brandSettings.tagline}`;
    
    // Update favicon if logo exists
    if (brandSettings.logo) {
      const favicon = document.getElementById('favicon');
      if (favicon) {
        favicon.href = brandSettings.logo;
      }
    }
  }, [brandSettings]);

  // Initialize Supabase connection and check if tables exist
  useEffect(() => {
    async function initializeSupabase() {
      try {
        console.log("Checking Supabase connection...");
        const { data, error } = await supabase
          .from(COUNTRIES_TABLE)
          .select("count()", { count: 'exact', head: true });

        if (error) {
          console.error("Supabase connection error:", error);
        } else {
          console.log("Supabase connected successfully");
        }
      } catch (error) {
        console.error("Failed to initialize Supabase:", error);
      }
    }

    initializeSupabase();

    // Load view preference from localStorage
    const savedViewMode = localStorage.getItem('viewMode');
    if (savedViewMode) {
      setViewMode(savedViewMode);
    } else {
      // If no saved preference, set list as default and save it
      localStorage.setItem('viewMode', 'list');
    }
  }, []);

  // Add keyboard event listener for Ctrl+S shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+S (Windows/Linux) or Command+S (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // Prevent the browser's save dialog
        setShowAdminButtons(true); // Show admin buttons
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Fetch countries from Supabase
  useEffect(() => {
    async function fetchCountries() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from(COUNTRIES_TABLE)
          .select('*')
          .order('name');

        if (error) {
          console.error('Error fetching countries:', error);
          return;
        }

        if (data && data.length > 0) {
          setCountryList(data);
          // Auto-select Saudi Arabia or the first country
          const saudiArabia = data.find(country => country.code === 'SA') || data[0];
          setSelectedCountry(saudiArabia);
          setCountrySelected(true);
        }
      } catch (error) {
        console.error('Error in fetchCountries:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchCountries();
  }, []);

  // Fetch brand settings from Supabase
  useEffect(() => {
    async function fetchBrandSettings() {
      try {
        const { data, error } = await supabase
          .from(BRAND_SETTINGS_TABLE)
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching brand settings:', error);
          return;
        }

        if (data && data.length > 0) {
          // Map database field names to component field names
          const settings = {
            name: data[0].name || 'CouponHub',
            tagline: data[0].tagline || 'Best deals in your region',
            logo: data[0].logo || '',
            primaryColor: data[0].primary_color || '#6366F1',
            secondaryColor: data[0].secondary_color || '#8B5CF6'
          };
          
          setBrandSettings(settings);
          
          // Update title and favicon immediately
          document.title = `${settings.name} - ${settings.tagline}`;
          
          if (settings.logo) {
            const favicon = document.getElementById('favicon');
            if (favicon) {
              favicon.href = settings.logo;
            }
          }
        }
      } catch (error) {
        console.error('Error in fetchBrandSettings:', error);
      }
    }

    fetchBrandSettings();
  }, []);

  // Fetch coupons for selected country
  useEffect(() => {
    async function fetchCoupons() {
      if (!selectedCountry) return;

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from(COUPONS_TABLE)
          .select('*')
          .eq('country_code', selectedCountry.code)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching coupons:', error);
          return;
        }

        // Map database field names to component field names
        const formattedCoupons = data?.map(coupon => ({
          id: coupon.id,
          brand: coupon.brand,
          title: coupon.title,
          description: coupon.description,
          discount: coupon.discount,
          code: coupon.code,
          logoImage: coupon.logo_image,
          storeUrl: coupon.store_url
        })) || [];

        // Update coupons state with the fetched data
        setCoupons(prevCoupons => ({
          ...prevCoupons,
          [selectedCountry.code]: formattedCoupons
        }));
      } catch (error) {
        console.error('Error in fetchCoupons:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchCoupons();
  }, [selectedCountry]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    setCountrySelected(true);
    setSearchQuery(''); // Clear search when changing country
  };

  const handleCopyCode = (code, couponId, storeUrl) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setActiveCoupon(couponId);
      setCopiedAnimation(true);

      // Reset copy animation after 300ms
      setTimeout(() => {
        setCopiedAnimation(false);
      }, 300);

      // Reset copied status after 2 seconds
      setTimeout(() => {
        setCopiedCode(null);
        // Redirect to store after showing copy feedback
        window.open(storeUrl, '_blank');
      }, 1000);
    });
  };

  // Toggle view mode between grid and list
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    // Save preference to localStorage
    localStorage.setItem('viewMode', mode);
  };

  // Function to clean and validate URLs
  const cleanAndValidateUrl = (url) => {
    if (!url) return '';
    let cleanUrl = url.trim();

    // Check if URL has protocol, if not add it
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }

    try {
      const urlObj = new URL(cleanUrl);
      return urlObj.href;
    } catch (e) {
      // If it's not a valid URL, try to extract URL parts
      const urlMatch = cleanUrl.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch && urlMatch[0]) {
        try {
          const extractedUrl = new URL(urlMatch[0]);
          return extractedUrl.href;
        } catch (e) {
          return '';
        }
      }
      return '';
    }
  };

  const handleImportData = async (importedData, shouldOverride) => {
    if (!importedData || Object.keys(importedData).length === 0) return;
    setLoading(true);

    try {
      // For each country in the imported data
      for (const countryCode of Object.keys(importedData)) {
        if (countryList.some(c => c.code === countryCode)) {
          // If the country exists in our list
          // If override mode is enabled, delete all existing coupons for this country first
          if (shouldOverride) {
            console.log(`Override mode: Deleting all coupons for ${countryCode}`);
            const { error: deleteError } = await supabase
              .from(COUPONS_TABLE)
              .delete()
              .eq('country_code', countryCode);

            if (deleteError) {
              console.error(`Error deleting coupons for ${countryCode}:`, deleteError);
            }
          }

          const couponsToInsert = importedData[countryCode].map(coupon => {
            // Ensure we're only storing proper URLs
            const cleanStoreUrl = cleanAndValidateUrl(coupon.storeUrl);
            return {
              country_code: countryCode,
              brand: coupon.brand,
              title: coupon.title,
              description: coupon.description,
              discount: coupon.discount,
              code: coupon.code,
              logo_image: coupon.logoImage,
              store_url: cleanStoreUrl
            };
          });

          // Insert coupons into Supabase
          const { error } = await supabase
            .from(COUPONS_TABLE)
            .insert(couponsToInsert);

          if (error) {
            console.error(`Error inserting coupons for ${countryCode}:`, error);
          } else {
            // Refresh coupons for this country
            const { data: updatedData } = await supabase
              .from(COUPONS_TABLE)
              .select('*')
              .eq('country_code', countryCode)
              .order('created_at', { ascending: false });

            // Map database field names to component field names
            const updatedCoupons = updatedData?.map(coupon => ({
              id: coupon.id,
              brand: coupon.brand,
              title: coupon.title,
              description: coupon.description,
              discount: coupon.discount,
              code: coupon.code,
              logoImage: coupon.logo_image,
              storeUrl: coupon.store_url
            })) || [];

            setCoupons(prev => ({
              ...prev,
              [countryCode]: updatedCoupons
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error importing data:', error);
    } finally {
      setLoading(false);
      setShowImporter(false);
    }
  };

  // Function to save a new or edited coupon
  const handleSaveCoupon = async (countryCode, coupon) => {
    setLoading(true);

    try {
      // Ensure we're only storing proper URLs
      const cleanStoreUrl = cleanAndValidateUrl(coupon.store_url);

      // Map component field names to database field names
      const couponData = {
        country_code: countryCode,
        brand: coupon.brand,
        title: coupon.title,
        description: coupon.description,
        discount: coupon.discount,
        code: coupon.code,
        logo_image: coupon.logo_image, // Use the database field name directly
        store_url: cleanStoreUrl // Use the cleaned URL
      };

      console.log("Saving coupon data:", couponData);

      let error;
      if (coupon.id) {
        // Update existing coupon
        const { error: updateError } = await supabase
          .from(COUPONS_TABLE)
          .update(couponData)
          .eq('id', coupon.id);
        error = updateError;
      } else {
        // Insert new coupon
        const { error: insertError } = await supabase
          .from(COUPONS_TABLE)
          .insert(couponData);
        error = insertError;
      }

      if (error) {
        console.error('Error saving coupon:', error);
        return;
      }

      // Refresh coupons for this country
      const { data: updatedData } = await supabase
        .from(COUPONS_TABLE)
        .select('*')
        .eq('country_code', countryCode)
        .order('created_at', { ascending: false });

      // Map database field names to component field names
      const updatedCoupons = updatedData?.map(coupon => ({
        id: coupon.id,
        brand: coupon.brand,
        title: coupon.title,
        description: coupon.description,
        discount: coupon.discount,
        code: coupon.code,
        logoImage: coupon.logo_image,
        storeUrl: coupon.store_url
      })) || [];

      setCoupons(prev => ({
        ...prev,
        [countryCode]: updatedCoupons
      }));
    } catch (error) {
      console.error('Error saving coupon:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to delete a coupon
  const handleDeleteCoupon = async (countryCode, couponId) => {
    setLoading(true);

    try {
      const { error } = await supabase
        .from(COUPONS_TABLE)
        .delete()
        .eq('id', couponId);

      if (error) {
        console.error('Error deleting coupon:', error);
        return;
      }

      // Refresh coupons for this country
      const { data: updatedData } = await supabase
        .from(COUPONS_TABLE)
        .select('*')
        .eq('country_code', countryCode)
        .order('created_at', { ascending: false });

      // Map database field names to component field names
      const updatedCoupons = updatedData?.map(coupon => ({
        id: coupon.id,
        brand: coupon.brand,
        title: coupon.title,
        description: coupon.description,
        discount: coupon.discount,
        code: coupon.code,
        logoImage: coupon.logo_image,
        storeUrl: coupon.store_url
      })) || [];

      setCoupons(prev => ({
        ...prev,
        [countryCode]: updatedCoupons
      }));
    } catch (error) {
      console.error('Error deleting coupon:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to add a new country
  const handleAddCountry = async (newCountry) => {
    // Check if country code already exists
    if (countryList.some(c => c.code === newCountry.code)) {
      return false;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from(COUNTRIES_TABLE)
        .insert({
          code: newCountry.code,
          name: newCountry.name,
          flag: newCountry.flag,
          currency: newCountry.currency
        });

      if (error) {
        console.error('Error adding country:', error);
        return false;
      }

      // Refresh country list
      const { data: updatedCountries } = await supabase
        .from(COUNTRIES_TABLE)
        .select('*')
        .order('name');

      setCountryList(updatedCountries || []);
      return true;
    } catch (error) {
      console.error('Error in handleAddCountry:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Function to edit a country
  const handleEditCountry = async (editedCountry) => {
    const { originalCode, ...countryData } = editedCountry;

    try {
      setLoading(true);
      const { error } = await supabase
        .from(COUNTRIES_TABLE)
        .update({
          code: countryData.code,
          name: countryData.name,
          flag: countryData.flag,
          currency: countryData.currency
        })
        .eq('code', originalCode);

      if (error) {
        console.error('Error updating country:', error);
        return false;
      }

      // If the code changed, update all coupons with the new code
      if (originalCode !== countryData.code) {
        const { error: updateError } = await supabase
          .from(COUPONS_TABLE)
          .update({ country_code: countryData.code })
          .eq('country_code', originalCode);

        if (updateError) {
          console.error('Error updating coupons country code:', updateError);
        }
      }

      // Refresh country list
      const { data: updatedCountries } = await supabase
        .from(COUNTRIES_TABLE)
        .select('*')
        .order('name');

      setCountryList(updatedCountries || []);

      // If this was the selected country, update it
      if (selectedCountry && selectedCountry.code === originalCode) {
        const updatedSelectedCountry = updatedCountries.find(c => c.code === countryData.code);
        if (updatedSelectedCountry) {
          setSelectedCountry(updatedSelectedCountry);
        }
      }

      return true;
    } catch (error) {
      console.error('Error in handleEditCountry:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Function to delete a country
  const handleDeleteCountry = async (countryCode) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from(COUNTRIES_TABLE)
        .delete()
        .eq('code', countryCode);

      if (error) {
        console.error('Error deleting country:', error);
        return false;
      }

      // Refresh country list
      const { data: updatedCountries } = await supabase
        .from(COUNTRIES_TABLE)
        .select('*')
        .order('name');

      setCountryList(updatedCountries || []);

      // If this was the selected country, select another one
      if (selectedCountry && selectedCountry.code === countryCode) {
        if (updatedCountries.length > 0) {
          setSelectedCountry(updatedCountries[0]);
        } else {
          setSelectedCountry(null);
        }
      }

      // Remove coupons for this country from state
      setCoupons(prev => {
        const newCoupons = { ...prev };
        delete newCoupons[countryCode];
        return newCoupons;
      });

      return true;
    } catch (error) {
      console.error('Error in handleDeleteCountry:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Function to save brand settings
  const handleSaveBrandSettings = async (newSettings) => {
    try {
      setLoading(true);
      const settingsData = {
        name: newSettings.name || brandSettings.name,
        tagline: newSettings.tagline || brandSettings.tagline,
        logo: newSettings.logo || brandSettings.logo,
        primary_color: newSettings.primaryColor || brandSettings.primary_color,
        secondary_color: newSettings.secondaryColor || brandSettings.secondary_color,
        updated_at: new Date().toISOString()
      };

      // Check if there's an existing record
      const { data: existingSettings } = await supabase
        .from(BRAND_SETTINGS_TABLE)
        .select('id')
        .limit(1);

      let error;
      if (existingSettings && existingSettings.length > 0) {
        // Update existing record
        const { error: updateError } = await supabase
          .from(BRAND_SETTINGS_TABLE)
          .update(settingsData)
          .eq('id', existingSettings[0].id);
        error = updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from(BRAND_SETTINGS_TABLE)
          .insert(settingsData);
        error = insertError;
      }

      if (error) {
        console.error('Error saving brand settings:', error);
        return;
      }

      // Update local state with normalized keys
      const updatedSettings = {
        name: settingsData.name,
        tagline: settingsData.tagline,
        logo: settingsData.logo,
        primaryColor: settingsData.primary_color,
        secondaryColor: settingsData.secondary_color
      };
      
      setBrandSettings(updatedSettings);
      
      // Update title and favicon immediately
      document.title = `${updatedSettings.name} - ${updatedSettings.tagline}`;
      
      if (updatedSettings.logo) {
        const favicon = document.getElementById('favicon');
        if (favicon) {
          favicon.href = updatedSettings.logo;
        }
      }

      // Save to localStorage as backup
      try {
        localStorage.setItem('brandSettings', JSON.stringify(updatedSettings));
      } catch (error) {
        console.error('Error saving brand settings to localStorage:', error);
      }
    } catch (error) {
      console.error('Error in handleSaveBrandSettings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking on Import or Manage buttons
  const handleImportClick = () => {
    if (isAuthenticated()) {
      setShowImporter(true);
    } else {
      setPendingAction('import');
      setShowLoginModal(true);
    }
  };

  const handleManageClick = () => {
    if (isAuthenticated()) {
      setShowControlPanel(true);
    } else {
      setPendingAction('manage');
      setShowLoginModal(true);
    }
  };

  // Handle successful login
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    if (pendingAction === 'import') {
      setShowImporter(true);
    } else if (pendingAction === 'manage') {
      setShowControlPanel(true);
    }
    setPendingAction(null);
  };

  // Filter coupons based on search query
  const getFilteredCoupons = () => {
    if (!selectedCountry || !coupons[selectedCountry.code]) {
      return [];
    }

    if (!searchQuery.trim()) {
      return coupons[selectedCountry.code];
    }

    const query = searchQuery.toLowerCase().trim();
    return coupons[selectedCountry.code].filter(coupon =>
      coupon.brand.toLowerCase().includes(query) ||
      coupon.title.toLowerCase().includes(query) ||
      coupon.description.toLowerCase().includes(query) ||
      coupon.code.toLowerCase().includes(query) ||
      coupon.discount.toLowerCase().includes(query)
    );
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Render grid view coupons
  const renderGridCoupons = (countryCoupons) => {
    return (
      <motion.div
        key={`${selectedCountry.code}-grid`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        {countryCoupons.map((coupon, index) => (
          <motion.div
            key={coupon.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-1 gap-2">
                    {coupon.logoImage && (
                      <div className="w-12 h-12 rounded-md overflow-hidden border">
                        <img
                          src={coupon.logoImage}
                          alt={`${coupon.brand} logo`}
                          className="w-full h-full object-contain bg-white"
                        />
                      </div>
                    )}
                    <div>
                      <span className="text-lg font-bold text-gray-800">{coupon.brand}</span>
                      <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full inline-block ml-2">
                        {coupon.discount}
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">{coupon.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{coupon.description}</p>
                </div>
              </div>

              {/* Coupon Code Section - Single Button for Copy & Redirect */}
              <motion.button
                onClick={() => handleCopyCode(coupon.code, coupon.id, coupon.storeUrl)}
                animate={activeCoupon === coupon.id && copiedAnimation ? { scale: 1.03 } : { scale: 1 }}
                transition={{ duration: 0.2 }}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all duration-300 ${copiedCode === coupon.code ? 'bg-green-500 hover:bg-green-600' : 'hover:opacity-90'} active:scale-98 flex items-center justify-between px-5 coupon-button`}
                style={{ background: copiedCode === coupon.code ? undefined : `linear-gradient(to right, ${brandSettings.primaryColor}, ${brandSettings.secondaryColor})` }}
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon
                    icon={copiedCode === coupon.code ? FiCheck : FiCopy}
                    className="text-white text-lg"
                  />
                  <div className="text-left">
                    <div className="font-mono font-bold tracking-wider">
                      {coupon.code}
                    </div>
                    <div className="text-xs text-white/80 mt-0.5">
                      {copiedCode === coupon.code ? "Copied! Redirecting..." : "Click to copy & shop"}
                    </div>
                  </div>
                </div>
                <SafeIcon
                  icon={FiExternalLink}
                  className={`text-white ${copiedCode === coupon.code ? 'animate-pulse' : ''}`}
                />
              </motion.button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Render list view coupons
  const renderListCoupons = (countryCoupons) => {
    return (
      <motion.div
        key={`${selectedCountry.code}-list`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-2"
      >
        {countryCoupons.map((coupon, index) => (
          <motion.div
            key={coupon.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
          >
            <div className="p-3">
              <div className="flex items-center gap-3">
                {/* Logo */}
                {coupon.logoImage && (
                  <div className="w-10 h-10 rounded-md overflow-hidden border flex-shrink-0">
                    <img
                      src={coupon.logoImage}
                      alt={`${coupon.brand} logo`}
                      className="w-full h-full object-contain bg-white"
                    />
                  </div>
                )}

                {/* Content - Updated for better mobile visibility */}
                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-800">{coupon.brand}</span>
                      <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {coupon.discount}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{coupon.title}</p>
                </div>

                {/* Copy Code Button */}
                <motion.button
                  onClick={() => handleCopyCode(coupon.code, coupon.id, coupon.storeUrl)}
                  animate={activeCoupon === coupon.id && copiedAnimation ? { scale: 1.03 } : { scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all ${copiedCode === coupon.code ? 'bg-green-500 hover:bg-green-600' : 'hover:opacity-90'} active:scale-98 flex items-center gap-2 flex-shrink-0`}
                  style={{ background: copiedCode === coupon.code ? undefined : `linear-gradient(to right, ${brandSettings.primaryColor}, ${brandSettings.secondaryColor})` }}
                >
                  <span className="font-mono font-bold tracking-wider whitespace-nowrap">
                    {coupon.code}
                  </span>
                  <SafeIcon
                    icon={copiedCode === coupon.code ? FiCheck : FiCopy}
                    className="text-white"
                  />
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Get filtered coupons based on search
  const filteredCoupons = getFilteredCoupons();

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom right, ${brandSettings.primaryColor}10, ${brandSettings.secondaryColor}10, ${brandSettings.primaryColor}20)` }}>
      {/* Loading Indicator */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[60]">
          <div className="bg-white p-4 rounded-xl shadow-lg flex items-center space-x-3">
            <SafeIcon icon={FiLoader} className="text-xl animate-spin text-blue-500" />
            <span className="font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-lg z-10">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `linear-gradient(to right, ${brandSettings.primaryColor}, ${brandSettings.secondaryColor})` }}
              >
                {brandSettings.logo ? (
                  <img
                    src={brandSettings.logo}
                    alt="Brand logo"
                    className="w-7 h-7 object-contain"
                    onError={(e) => {
                      console.error('Failed to load logo:', e);
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = `<div class="text-white text-xl"><svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path><path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path><path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path></svg></div>`;
                    }}
                  />
                ) : (
                  <SafeIcon icon={FiGift} className="text-white text-xl" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{brandSettings.name}</h1>
                <p className="text-xs text-gray-500">{brandSettings.tagline}</p>
              </div>
            </div>
            
            {/* Admin buttons only shown when showAdminButtons is true */}
            {showAdminButtons && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleImportClick}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: `${brandSettings.primaryColor}10`, color: brandSettings.primaryColor }}
                >
                  <SafeIcon icon={FiUpload} />
                  <span className="text-sm font-medium">Import</span>
                </button>
                <button
                  onClick={handleManageClick}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: `${brandSettings.secondaryColor}10`, color: brandSettings.secondaryColor }}
                >
                  <SafeIcon icon={FiSettings} />
                  <span className="text-sm font-medium">Manage</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Country Selector */}
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full bg-white rounded-2xl shadow-md border border-gray-100 p-4 flex items-center justify-between hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiMapPin} className="text-gray-400" />
              <div className="flex items-center space-x-2">
                {selectedCountry ? (
                  <>
                    <span className="text-2xl">{selectedCountry.flag}</span>
                    <span className="font-semibold text-gray-800">{selectedCountry.name}</span>
                  </>
                ) : (
                  <span className="font-semibold text-gray-800">Select a country</span>
                )}
              </div>
            </div>
            <motion.div
              animate={{ rotate: showDropdown ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiChevronDown} className="text-gray-400" />
            </motion.div>
          </button>

          <AnimatePresence>
            {showDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-40"
              >
                {countryList.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className="w-full p-4 flex items-center space-x-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
                  >
                    <span className="text-2xl">{country.flag}</span>
                    <span className="font-medium text-gray-800">{country.name}</span>
                    {selectedCountry && selectedCountry.code === country.code && (
                      <SafeIcon
                        icon={FiCheck}
                        style={{ color: brandSettings.primaryColor }}
                        className="ml-auto"
                      />
                    )}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Coupons Section */}
      <div className="max-w-md mx-auto px-4 pb-8">
        {selectedCountry ? (
          <>
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search coupons by brand, title, or code..."
                  className="w-full p-3 pl-10 pr-10 bg-white rounded-xl shadow-md border border-gray-100 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ focusRing: `${brandSettings.primaryColor}40` }}
                />
                <SafeIcon
                  icon={FiSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {searchQuery ? 'Search Results' : `Hot Deals in ${selectedCountry.name}`}
                </h2>
                <p className="text-gray-600">
                  {searchQuery
                    ? `Found ${filteredCoupons.length} matching coupons`
                    : 'Limited time offers just for you!'}
                </p>
              </div>

              {/* View Toggle Buttons */}
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleViewMode('grid')}
                  className={`p-2 flex items-center justify-center ${viewMode === 'grid' ? 'bg-gray-100 text-gray-800' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  title="Grid view"
                >
                  <SafeIcon icon={FiGrid} className="text-lg" />
                </button>
                <button
                  onClick={() => toggleViewMode('list')}
                  className={`p-2 flex items-center justify-center ${viewMode === 'list' ? 'bg-gray-100 text-gray-800' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  title="List view"
                >
                  <SafeIcon icon={FiList} className="text-lg" />
                </button>
              </div>
            </div>

            {filteredCoupons.length > 0 ? (
              <AnimatePresence mode="wait">
                {viewMode === 'grid' ? (
                  renderGridCoupons(filteredCoupons)
                ) : (
                  renderListCoupons(filteredCoupons)
                )}
              </AnimatePresence>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl shadow-md">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  {searchQuery ? (
                    <SafeIcon icon={FiSearch} className="text-gray-400 text-xl" />
                  ) : (
                    <SafeIcon icon={FiGift} className="text-gray-400 text-xl" />
                  )}
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {searchQuery ? 'No Matching Coupons' : 'No Coupons Yet'}
                </h3>
                <p className="text-gray-500 max-w-xs mx-auto mb-6">
                  {searchQuery
                    ? `No coupons match your search for "${searchQuery}"`
                    : `There are no coupons available for ${selectedCountry.name} yet.`}
                </p>
                {searchQuery ? (
                  <button
                    onClick={handleClearSearch}
                    className="px-4 py-2 text-white rounded-lg transition-colors inline-flex items-center"
                    style={{ backgroundColor: brandSettings.primaryColor }}
                  >
                    <SafeIcon icon={FiX} className="mr-2" />
                    Clear Search
                  </button>
                ) : (
                  showAdminButtons && (
                    <button
                      onClick={handleManageClick}
                      className="px-4 py-2 text-white rounded-lg transition-colors inline-flex items-center"
                      style={{ backgroundColor: brandSettings.primaryColor }}
                    >
                      <SafeIcon icon={FiPlus} className="mr-2" />
                      Add Coupons
                    </button>
                  )
                )}
              </div>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <SafeIcon icon={FiMapPin} className="text-gray-400 text-xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Country</h3>
            <p className="text-gray-500 max-w-xs mx-auto">
              Please select a country from the dropdown above to view available coupons
            </p>
          </motion.div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            More deals coming soon! 🎉
          </p>
        </div>
      </div>

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <LoginModal
            onClose={() => {
              setShowLoginModal(false);
              setPendingAction(null);
            }}
            onLoginSuccess={handleLoginSuccess}
          />
        )}
      </AnimatePresence>

      {/* Excel Importer Modal */}
      <AnimatePresence>
        {showImporter && (
          <ExcelImporter
            onClose={() => setShowImporter(false)}
            onImport={handleImportData}
            countries={countryList}
          />
        )}
      </AnimatePresence>

      {/* Control Panel Modal */}
      <AnimatePresence>
        {showControlPanel && (
          <ControlPanel
            countries={countryList}
            coupons={coupons}
            brandSettings={brandSettings}
            onSaveCoupon={handleSaveCoupon}
            onDeleteCoupon={handleDeleteCoupon}
            onAddCountry={handleAddCountry}
            onEditCountry={handleEditCountry}
            onDeleteCountry={handleDeleteCountry}
            onSaveBrandSettings={handleSaveBrandSettings}
            onClose={() => setShowControlPanel(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;